'use client';

import { useReducer, useRef } from 'react';
import { IconCircleAlert } from '@pie/amber-icons';
import {
  Button,
  Checkbox,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogHeading,
  DialogRoot,
  ErrorState,
  Link,
  Stack,
  Text,
} from '@pie/amber';
import { track } from '@upms/analytics';
import { reporter } from '@upms/observability';
import { CONSENT_EVENTS } from '@/lib/analytics/events';
import { CommunicationPreferences } from './CommunicationPreferences';
import { consentFormReducer, INITIAL_CONSENT_FORM_STATE } from './consentFormReducer';
import type { ConsentPrompt } from './deriveConsentPrompt';
import { useConsentPrompt } from './useConsentPrompt';
import { useConsentSubmission } from './useConsentSubmission';

const ELECTRONIC_DELIVERY_CONSENT_URL =
  'https://www.pieinsurance.com/legal/terms-of-use/electronic-delivery';

const LEGAL_LINK_PROPS = {
  external: true,
  rel: 'noopener noreferrer',
  target: '_blank',
} as const;

const FAILURE_MESSAGE =
  'Something went wrong on our end. Your selections are still here. Please try again.';

type ContentProps = {
  prompt: Extract<ConsentPrompt, { kind: 'prompt' }>;
  onOpenChange: (open: boolean) => void;
};

const ConsentToContactDialogContent = ({ prompt, onOpenChange }: ContentProps) => {
  const [formState, dispatch] = useReducer(consentFormReducer, INITIAL_CONSENT_FORM_STATE);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const { title, description, showElectronicDelivery, missing } = prompt;

  const focusPhoneField = () => {
    phoneInputRef.current?.focus();
    phoneInputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const handleMobilePhoneError = (message: string) => {
    dispatch({ type: 'SET_MOBILE_PHONE_ERROR', message });
    focusPhoneField();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (open) return;
    track(CONSENT_EVENTS.consentPromptDismissed, { pending_types: missing.join(',') });
    reporter.action('consent_prompt_closed', { feature: 'consent', outcome: 'dismissed' });
    onOpenChange(false);
  };

  const { submit, status } = useConsentSubmission({
    prompt,
    onMobilePhoneError: handleMobilePhoneError,
    onSubmitted: () => onOpenChange(false),
    onNothingToSubmit: () => handleDialogOpenChange(false),
  });

  return (
    <DialogRoot
      open
      onOpenChange={handleDialogOpenChange}
      size="lg"
      position={{ base: 'bottom', sm: 'center' }}
    >
      <DialogContent closeOnOutsideInteraction={status !== 'pending'}>
        <DialogHeader>
          <DialogHeading>{title}</DialogHeading>
          <DialogClose />
        </DialogHeader>
        <DialogBody>
          <Stack gap="md">
            <Text variant="body-sm">{description}</Text>

            {status === 'error' && (
              <ErrorState size="sm" icon={<IconCircleAlert />} helpText={FAILURE_MESSAGE} />
            )}

            {showElectronicDelivery && (
              <Checkbox
                checked={formState.values.electronicDelivery}
                onCheckedChange={() =>
                  dispatch({ type: 'TOGGLE', consentType: 'ELECTRONIC_DELIVERY' })
                }
                label="Electronic delivery"
                helpText={
                  <>
                    By accepting our{' '}
                    <Link {...LEGAL_LINK_PROPS} href={ELECTRONIC_DELIVERY_CONSENT_URL}>
                      Electronic Delivery Consent
                    </Link>{' '}
                    you will be able to receive all mailings and communications electronically.
                  </>
                }
              />
            )}

            <CommunicationPreferences
              values={formState.values}
              showElectronicDelivery={showElectronicDelivery}
              showTextAlerts={prompt.showTextAlerts}
              showAutomatedCalling={prompt.showAutomatedCalling}
              onToggle={(consentType) => dispatch({ type: 'TOGGLE', consentType })}
              phoneRef={phoneInputRef}
              phoneError={formState.mobilePhoneError}
              onPhoneChange={(value) => dispatch({ type: 'SET_PHONE', value })}
            />
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Stack direction="row" justify="end">
            <Button
              tone="accent"
              emphasis="heavy"
              loading={status === 'pending'}
              onClick={() => submit(formState.values)}
              fluid={{ base: true, sm: false }}
            >
              Save Preferences
            </Button>
          </Stack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export const ConsentToContactDialog = () => {
  const consentPrompt = useConsentPrompt();

  if (!consentPrompt.open) return null;

  return (
    <ConsentToContactDialogContent
      prompt={consentPrompt.prompt}
      onOpenChange={consentPrompt.onOpenChange}
    />
  );
};
